import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import {
  ApiV1FindingService,
  IntakeStatus,
  RequirementIntake,
  SaveVerdictRequest,
  VerdictResult,
} from 'api';
import {
  ApplicabilityChangeEvent,
  ApplicabilitySectionComponent,
} from './applicability-section/applicability-section.component';
import { EvidenceSlotsComponent } from './evidence-slots/evidence-slots.component';
import { IntakeStatePillComponent } from 'component/details/requirement-intake/intake-state-pill/intake-state-pill.component';
import { ExamplesComponent } from './examples/examples.component';
import { FindingsComponent } from './findings/findings.component';
import { VerdictWidgetComponent } from './verdict-widget/verdict-widget.component';
import { TranslateModule } from '@ngx-translate/core';
import { ReferenceElementsComponent } from 'component/details/requirement-intake/requirements-detail/reference-elements/reference-elements.component';
import { DomSanitizer } from '@angular/platform-browser';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, distinctUntilChanged, map, of, switchMap } from 'rxjs';

@Component({
  selector: 'comp-requirements-detail',
  templateUrl: './requirements-detail.component.html',
  styleUrls: ['./requirements-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ApplicabilitySectionComponent,
    EvidenceSlotsComponent,
    IntakeStatePillComponent,
    ExamplesComponent,
    FindingsComponent,
    VerdictWidgetComponent,
    TranslateModule,
    ReferenceElementsComponent,
  ],
})
export class RequirementsDetailComponent {
  readonly domSanitizer = inject(DomSanitizer);
  private readonly findingService = inject(ApiV1FindingService);

  readonly requirement = input<RequirementIntake | undefined>(undefined);
  readonly category = input<string | undefined>(undefined);
  readonly isLoading = input<boolean>(false);
  readonly hasError = input<boolean>(false);
  readonly isSubmitPending = input<boolean>(false);
  readonly actionMessage = input<string | undefined>(undefined);
  readonly applicabilityConfirmed = output<ApplicabilityChangeEvent>();
  readonly verdictSaveRequested = output<{ requirementId: number; command: SaveVerdictRequest }>();
  readonly notifyReloadNeeded = output<number>();

  protected readonly hasSelection = computed(() => this.requirement() !== undefined);
  protected readonly expandedSections = signal({
    examples: false,
    referenceElements: false,
  });

  protected readonly findingsRefreshTrigger = signal(0);

  /** Snapshot of the default-open state captured when the requirement ID changes. */
  protected readonly defaultCollapsed = signal(false);

  /** Tracks only the requirement identity, so backend refreshes of the same
   *  requirement do NOT trigger a reset of the open/collapsed state. */
  private readonly requirementId = computed(() => this.requirement()?.id);

  private readonly findingsQuery = computed(() => ({
    requirementId: this.requirementId(),
    requirementEvidences: this.requirement()?.evidences,
  }));

  protected readonly hasFindings = toSignal(
    toObservable(this.findingsQuery).pipe(
      distinctUntilChanged(
        (previous, current) =>
          previous.requirementId === current.requirementId &&
          previous.requirementEvidences === current.requirementEvidences,
      ),
      switchMap(({ requirementId }) => {
        if (requirementId === undefined) {
          return of(false);
        }

        return this.findingService.getFindingsForRequirementApplication(requirementId, 0, 1).pipe(
          map((result) => {
            const hasFindingItems = (result.items?.length ?? 0) > 0;
            if (hasFindingItems) {
              this.findingsRefreshTrigger.update((current) => current + 1);
            }
            return hasFindingItems;
          }),
          catchError(() => of(false)),
        );
      }),
    ),
    { initialValue: false },
  );

  constructor() {
    // Reset expand overrides only when the selected requirement *identity* changes.
    effect(() => {
      this.requirementId(); // tracked — fires only on ID change
      // do not trigger this effect on IntakeStatus Change, only retrieve the value in case requirement changes
      const initiallyOpen = untracked(() => this.isEvidenceIntakeInProgress());
      this.defaultCollapsed.set(!initiallyOpen);
    });
  }

  protected toggleSectionExpanded(section: 'examples' | 'referenceElements'): void {
    this.expandedSections.update((expanded) => {
      console.log(expanded);
      return {
        ...expanded,
        [section]: !expanded[section],
      };
    });
  }
  protected readonly effectiveStatus = computed(
    () => this.requirement()?.intakeStatus ?? IntakeStatus.Incomplete,
  );

  protected readonly htmlDescription = computed(() =>
    this.domSanitizer.bypassSecurityTrustHtml(this.requirement()?.description ?? ''),
  );

  //check
  protected readonly effectiveVerdictResult = computed<VerdictResult | undefined>(() => {
    const verdict = this.requirement()?.verdict;
    const value = verdict?.finalResult ?? verdict?.proposedResult;

    if (value === VerdictResult.Pass) {
      return VerdictResult.Pass;
    }

    if (value === VerdictResult.Fail) {
      return VerdictResult.Fail;
    }

    return undefined;
  });

  protected resultLabelKey(result: VerdictResult): string {
    return result === VerdictResult.Pass
      ? 'requirementIntake.verdict.result.pass'
      : 'requirementIntake.verdict.result.fail';
  }

  protected readonly showVerdictHeaderIndicator = computed(() => {
    return (
      this.effectiveStatus() === IntakeStatus.Decided && this.effectiveVerdictResult() !== undefined
    );
  });

  protected readonly showIntakeStatusPill = computed(() => {
    return !this.showVerdictHeaderIndicator();
  });

  protected readonly showFindingsSection = computed(() => {
    const effectiveStatus = this.effectiveStatus();

    if (effectiveStatus === IntakeStatus.ConfirmReuse) {
      return false;
    }

    return (
      this.hasFindings() ||
      effectiveStatus === IntakeStatus.ReviewFindings ||
      effectiveStatus === IntakeStatus.Decided
    );
  });

  protected readonly showVerdictWidget = computed(() => {
    const effectiveStatus = this.effectiveStatus();
    if (effectiveStatus === IntakeStatus.ConfirmReuse) {
      return false;
    }

    const requirement = this.requirement();
    return (
      requirement !== undefined &&
      requirement.applicability !== false &&
      requirement.evidences.some((evidence) => evidence.fileAttachments.length > 0) &&
      effectiveStatus !== IntakeStatus.NotApplicable
    );
  });

  protected readonly isEvidenceIntakeInProgress = computed(
    () =>
      this.effectiveStatus() === IntakeStatus.Incomplete ||
      this.effectiveStatus() == IntakeStatus.PendingAnalysis ||
      this.effectiveStatus() == IntakeStatus.ConfirmReuse,
  );

  protected readonly IntakeStatus = IntakeStatus;
}
